import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';

const STORAGE_KEY = 'lastSeenVersion';

/**
 * Parses the CHANGELOG.md section belonging to the given version.
 * Sections are headed `## [VERSION] - YYYY-MM-DD`; a section ends at the next
 * `## [` heading. Returns the content lines, or an empty array when the version
 * has no section or its section carries no release notes (only group headers).
 * @since NEXT_VERSION
 */
function parseSectionForVersion(markdown, version) {
  if (!version) return [];

  const lines = markdown.split('\n');
  const isHeading = (l) => /^## \[/.test(l);
  const start = lines.findIndex(
    (l) => isHeading(l) && l.match(/^## \[([^\]]+)\]/)?.[1] === version
  );
  if (start === -1) return [];

  const end = lines.findIndex((l, i) => i > start && isHeading(l));
  const contentLines = lines
    .slice(start + 1, end === -1 ? undefined : end)
    // Drop blank lines and the '---' rules that separate CHANGELOG sections
    .filter((l) => l.trim() !== '' && !/^-{3,}$/.test(l.trim()));

  // A section with headers but no bullet items carries no notes worth showing
  const hasItems = contentLines.some((l) => l.startsWith('- '));
  return hasItems ? contentLines : [];
}

/**
 * Renders a single line from the CHANGELOG with appropriate MUI typography.
 * @since NEXT_VERSION
 */
function ChangelogLine({ line }) {
  if (line.startsWith('### ')) {
    return (
      <Typography variant="subtitle2" fontWeight="bold" mt={1} mb={0.5}>
        {line.replace('### ', '')}
      </Typography>
    );
  }
  if (line.startsWith('- ')) {
    return (
      <Typography variant="body2" component="li" sx={{ ml: 2, mb: 0.25 }}>
        {line.replace(/^- /, '')}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" mb={0.5}>
      {line}
    </Typography>
  );
}

/**
 * Shows a "What's New" dialog on first login after a version upgrade.
 * Only the running version's own CHANGELOG section is shown — when that
 * version has no section (or no release notes in it), the dialog stays closed
 * rather than repeating an earlier release's notes.
 * Skipped entirely in dev builds (appVersion starts with 'dev', covers both
 * local default 'dev' and CI-stamped 'dev-SNAPSHOT').
 * Dismissed state stored in localStorage under 'lastSeenVersion'.
 * @since NEXT_VERSION
 */
export function WhatsNewModal({ appVersion }) {
  const [open, setOpen] = useState(false);
  const [changelogLines, setChangelogLines] = useState([]);

  useEffect(() => {
    if (appVersion?.startsWith('dev')) return;

    const lastSeen = localStorage.getItem(STORAGE_KEY);
    if (lastSeen === appVersion) return;

    fetch('/CHANGELOG.md')
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((text) => {
        const lines = parseSectionForVersion(text, appVersion);
        if (lines.length > 0) {
          setChangelogLines(lines);
          setOpen(true);
        }
      })
      .catch(() => {
        // CHANGELOG not available — skip modal silently
      });
  }, [appVersion]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, appVersion);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          What's New in v{appVersion}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {changelogLines.map((line, i) => (
            <ChangelogLine key={i} line={line} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="primary">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
